export const BUILT_IN_COMMON_MEASURE_DEFINITIONS = Object.freeze([
  {
    id: "ml",
    defaultLabel: "mL",
    defaultPluralLabel: "mL",
    labelKey: "measureMl-string",
    pluralLabelKey: "measureMlPlural-string",
    keywords: [],
    aliases: ["mL"],
  },
  {
    id: "oz",
    defaultLabel: "oz",
    defaultPluralLabel: "oz",
    labelKey: "measureOz-string",
    pluralLabelKey: "measureOzPlural-string",
    keywords: [],
    aliases: [],
  },
  {
    id: "fl oz",
    defaultLabel: "fl oz",
    defaultPluralLabel: "fl oz",
    labelKey: "measureFlOz-string",
    pluralLabelKey: "measureFlOzPlural-string",
    keywords: [],
    aliases: ["fl. oz"],
  },
  {
    id: "cup",
    defaultLabel: "Cup",
    defaultPluralLabel: "Cups",
    labelKey: "measureCup-string",
    pluralLabelKey: "measureCupPlural-string",
    keywords: [],
    aliases: [],
  },
  {
    id: "glass",
    defaultLabel: "Glass",
    defaultPluralLabel: "Glasses",
    labelKey: "measureGlass-string",
    pluralLabelKey: "measureGlassPlural-string",
    keywords: [],
    aliases: [],
  },
  {
    id: "tbsp",
    defaultLabel: "Tablespoon",
    defaultPluralLabel: "Tablespoons",
    labelKey: "measureTbsp-string",
    pluralLabelKey: "measureTbspPlural-string",
    keywords: ["tablespoon"],
    aliases: [],
  },
  {
    id: "tsp",
    defaultLabel: "Teaspoon",
    defaultPluralLabel: "Teaspoons",
    labelKey: "measureTsp-string",
    pluralLabelKey: "measureTspPlural-string",
    keywords: ["teaspoon"],
    aliases: [],
  },
  {
    id: "piece",
    defaultLabel: "Piece",
    defaultPluralLabel: "Pieces",
    labelKey: "measurePiece-string",
    pluralLabelKey: "measurePiecePlural-string",
    keywords: [],
    aliases: [],
  },
  {
    id: "unit",
    defaultLabel: "Unit",
    defaultPluralLabel: "Units",
    labelKey: "measureUnit-string",
    pluralLabelKey: "measureUnitPlural-string",
    keywords: [],
    aliases: [],
  },
  {
    id: "slice",
    defaultLabel: "Slice",
    defaultPluralLabel: "Slices",
    labelKey: "measureSlice-string",
    pluralLabelKey: "measureSlicePlural-string",
    keywords: ["bread"],
    aliases: [],
  },
  {
    id: "serving",
    defaultLabel: "Serving",
    defaultPluralLabel: "Servings",
    labelKey: "measureServing-string",
    pluralLabelKey: "measureServingPlural-string",
    keywords: ["portion"],
    aliases: [],
  },
  {
    id: "scoop",
    defaultLabel: "Scoop",
    defaultPluralLabel: "Scoops",
    labelKey: "measureScoop-string",
    pluralLabelKey: "measureScoopPlural-string",
    keywords: ["powder"],
    aliases: [],
  },
  {
    id: "small",
    defaultLabel: "Small",
    defaultPluralLabel: "Smalls",
    labelKey: "measureSmall-string",
    pluralLabelKey: "measureSmallPlural-string",
    keywords: [],
    aliases: [],
  },
  {
    id: "medium",
    defaultLabel: "Medium",
    defaultPluralLabel: "Mediums",
    labelKey: "measureMedium-string",
    pluralLabelKey: "measureMediumPlural-string",
    keywords: [],
    aliases: [],
  },
  {
    id: "large",
    defaultLabel: "Large",
    defaultPluralLabel: "Larges",
    labelKey: "measureLarge-string",
    pluralLabelKey: "measureLargePlural-string",
    keywords: [],
    aliases: [],
  },
  {
    id: "bottle",
    defaultLabel: "Bottle",
    defaultPluralLabel: "Bottles",
    labelKey: "measureBottle-string",
    pluralLabelKey: "measureBottlePlural-string",
    keywords: [],
    aliases: [],
  },
  {
    id: "can",
    defaultLabel: "Can",
    defaultPluralLabel: "Cans",
    labelKey: "measureCan-string",
    pluralLabelKey: "measureCanPlural-string",
    keywords: [],
    aliases: [],
  },
]);

const BUILT_IN_COMMON_MEASURE_LOOKUP = BUILT_IN_COMMON_MEASURE_DEFINITIONS.reduce(
  (lookup, definition) => {
    const normalizedId = String(definition.id ?? "").trim().toLowerCase();
    if (!normalizedId) {
      return lookup;
    }

    lookup.set(normalizedId, definition);
    (Array.isArray(definition.aliases) ? definition.aliases : []).forEach((alias) => {
      const normalizedAlias = String(alias ?? "").trim().toLowerCase();
      if (normalizedAlias) {
        lookup.set(normalizedAlias, definition);
      }
    });

    return lookup;
  },
  new Map(),
);

function normalizeCommonMeasureLookupValue(value, fallback = "") {
  return String(value ?? fallback)
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function getBuiltInCommonMeasureDefinition(value, fallback = "") {
  const normalizedValue = normalizeCommonMeasureLookupValue(value, fallback);

  if (!normalizedValue) {
    return null;
  }

  return BUILT_IN_COMMON_MEASURE_LOOKUP.get(normalizedValue) ?? null;
}

function humanizeCommonMeasureId(value) {
  return String(value ?? "")
    .trim()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getMeasureLabelCountValue(value) {
  if (typeof value === "string") {
    const normalizedValue = value.trim().replace(",", ".");
    if (!normalizedValue) {
      return null;
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

export function shouldUsePluralMeasureLabel(count) {
  const normalizedCount = getMeasureLabelCountValue(count);

  if (normalizedCount == null) {
    return false;
  }

  return Math.abs(normalizedCount - 1) >= 0.0001;
}

export function normalizeCommonMeasureId(value, fallback = "") {
  const normalizedValue = normalizeCommonMeasureLookupValue(value, fallback);

  if (!normalizedValue) {
    return "";
  }

  return BUILT_IN_COMMON_MEASURE_LOOKUP.get(normalizedValue)?.id ?? normalizedValue;
}

export function normalizeBuiltInCommonMeasureId(value, fallback = "") {
  return getBuiltInCommonMeasureDefinition(value, fallback)?.id ?? "";
}

export function isBuiltInCommonMeasureId(value, fallback = "") {
  return normalizeBuiltInCommonMeasureId(value, fallback).length > 0;
}

export function getCommonMeasureKey(value, fallback = "") {
  const resolvedValue =
    value && typeof value === "object" ? value?.measureKey ?? value?.id : value;

  return normalizeCommonMeasureId(resolvedValue, fallback);
}

export function getCommonMeasureDisplayLabel(measureId, fallbackLabel = "") {
  const normalizedMeasureId = normalizeCommonMeasureId(measureId);
  const builtInDefinition = getBuiltInCommonMeasureDefinition(normalizedMeasureId);

  if (builtInDefinition?.defaultLabel) {
    return builtInDefinition.defaultLabel;
  }

  const normalizedFallbackLabel = String(fallbackLabel ?? "").trim();
  if (normalizedFallbackLabel) {
    return normalizedFallbackLabel;
  }

  return humanizeCommonMeasureId(normalizedMeasureId || measureId);
}

export function getLocalizedCommonMeasureDisplayLabel(
  measureId,
  { t, fallbackLabel = "" } = {},
) {
  const normalizedMeasureId = normalizeCommonMeasureId(measureId);
  const builtInDefinition = getBuiltInCommonMeasureDefinition(normalizedMeasureId);
  const normalizedFallbackLabel = String(fallbackLabel ?? "").trim();

  if (builtInDefinition?.labelKey && typeof t === "function") {
    return t(
      builtInDefinition.labelKey,
      normalizedFallbackLabel || builtInDefinition.defaultLabel,
    );
  }

  return getCommonMeasureDisplayLabel(normalizedMeasureId, normalizedFallbackLabel);
}

export function getCommonMeasureQuantityLabel(
  measureId,
  { fallbackLabel = "", count, preferPlural = false } = {},
) {
  const normalizedMeasureId = normalizeCommonMeasureId(measureId);
  const builtInDefinition = getBuiltInCommonMeasureDefinition(normalizedMeasureId);
  const normalizedFallbackLabel = String(fallbackLabel ?? "").trim();
  const usePlural = preferPlural || shouldUsePluralMeasureLabel(count);

  if (builtInDefinition) {
    return usePlural
      ? builtInDefinition.defaultPluralLabel ?? builtInDefinition.defaultLabel
      : builtInDefinition.defaultLabel;
  }

  return getCommonMeasureDisplayLabel(normalizedMeasureId, normalizedFallbackLabel);
}

export function getLocalizedCommonMeasureQuantityLabel(
  measureId,
  { t, fallbackLabel = "", count, preferPlural = false } = {},
) {
  const normalizedMeasureId = normalizeCommonMeasureId(measureId);
  const builtInDefinition = getBuiltInCommonMeasureDefinition(normalizedMeasureId);
  const normalizedFallbackLabel = String(fallbackLabel ?? "").trim();
  const usePlural = preferPlural || shouldUsePluralMeasureLabel(count);

  if (builtInDefinition && typeof t === "function") {
    const labelKey = usePlural
      ? builtInDefinition.pluralLabelKey ?? builtInDefinition.labelKey
      : builtInDefinition.labelKey;
    const fallbackValue = usePlural
      ? builtInDefinition.defaultPluralLabel ?? builtInDefinition.defaultLabel
      : builtInDefinition.defaultLabel;

    return t(labelKey, normalizedFallbackLabel || fallbackValue);
  }

  return getCommonMeasureQuantityLabel(normalizedMeasureId, {
    fallbackLabel: normalizedFallbackLabel,
    count,
    preferPlural,
  });
}

export function getLocalizedCommonMeasureDisplayLabelForMeasure(
  value,
  { t, fallbackLabel = "" } = {},
) {
  return getLocalizedCommonMeasureDisplayLabel(getCommonMeasureKey(value), {
    t,
    fallbackLabel,
  });
}

export function getLocalizedCommonMeasureQuantityLabelForMeasure(
  value,
  { t, fallbackLabel = "", count, preferPlural = false } = {},
) {
  return getLocalizedCommonMeasureQuantityLabel(getCommonMeasureKey(value), {
    t,
    fallbackLabel,
    count,
    preferPlural,
  });
}

export function getInlineCommonMeasureLabel(label) {
  return String(label ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => (/^[A-Z][a-z]+$/.test(word) ? word.toLowerCase() : word))
    .join(" ");
}

export function getBuiltInCommonMeasureItems(t) {
  return BUILT_IN_COMMON_MEASURE_DEFINITIONS.map((definition) => ({
    id: definition.id,
    label:
      typeof t === "function"
        ? t(definition.labelKey, definition.defaultLabel)
        : definition.defaultLabel,
    keywords: definition.keywords,
  }));
}
